import createCachedSelector from 're-reselect';

import { DelegationTeam, EntityStatus, Experiment, User, YammerState, getStatus } from '../../state/types';
import { getExperimentTreatment } from '../experiments/selectors';
import { getCurrentUser, getFulfilledUsers } from '../users/selectors';

type GetCurrentDelegationTeams = (state: YammerState) => Record<User['id'], DelegationTeam> | undefined;
export const getCurrentDelegationTeams: GetCurrentDelegationTeams = (state: YammerState) =>
  state.delegationTeams.fulfilled;

type GetCurrentDelegationTeamsStatus = (state: YammerState) => EntityStatus;
export const getCurrentDelegationTeamsStatus: GetCurrentDelegationTeamsStatus = (state) =>
  getStatus(state.delegationTeams);

type GetOwnDelegationTeam = (state: YammerState, userId: User['id']) => DelegationTeam | undefined;
export const getOwnDelegationTeam: GetOwnDelegationTeam = createCachedSelector(
  (_state: YammerState, userId: User['id']) => userId,
  getCurrentDelegationTeams,
  (userId, currentDelegationTeams) => {
    if (!currentDelegationTeams) {
      return undefined;
    }

    return currentDelegationTeams[userId];
  }
)((_state, userId) => userId);

type GetOtherDelegationTeams = (state: YammerState, userId: User['id']) => DelegationTeam[];
export const getOtherDelegationTeams: GetOtherDelegationTeams = createCachedSelector(
  (_state: YammerState, userId: User['id']) => userId,
  getCurrentDelegationTeams,
  (userId, currentDelegationTeams) => {
    if (!currentDelegationTeams) {
      return [];
    }

    const { [userId]: ownDelegationTeam, ...otherDelegationTeams } = currentDelegationTeams;

    return Object.values(otherDelegationTeams);
  }
)((_state, userId) => userId);

type GetDelegationTeamUsersForCurrentUser = (state: YammerState) => User[];
export const getDelegationTeamUsersForCurrentUser: GetDelegationTeamUsersForCurrentUser = createCachedSelector(
  getCurrentDelegationTeams,
  getCurrentUser,
  getFulfilledUsers,
  (onBehalfOfSenders, currentUser, users) => {
    if (!onBehalfOfSenders) {
      return [currentUser];
    }

    return Object.values(onBehalfOfSenders).reduce((senders, user) => {
      const sender = users[user.ownerId];

      if (sender) {
        return [...senders, sender];
      }

      return senders;
    }, []);
  }
)((_state) => 'cache');

type GetDelegationTeamUsersForCurrentUserStoryline = (state: YammerState, experiment: Experiment) => User[];
export const getDelegationTeamUsersForCurrentUserStoryline: GetDelegationTeamUsersForCurrentUserStoryline =
  createCachedSelector(
    getCurrentDelegationTeams,
    getCurrentUser,
    getFulfilledUsers,
    getExperimentTreatment,
    (onBehalfOfSenders, currentUser, users, poboStorylineFilesEnabled) => {
      if (!onBehalfOfSenders) {
        return [currentUser];
      }

      if (poboStorylineFilesEnabled) {
        return Object.values(onBehalfOfSenders).reduce((senders, user) => {
          const sender = users[user.ownerId];
          const senderHasPermission = user.viewerCanPostToStoryline || sender?.id === currentUser.id;

          if (sender && senderHasPermission) {
            return [...senders, sender];
          }

          return senders;
        }, []);
      }

      return Object.values(onBehalfOfSenders).reduce((senders, user) => {
        const sender = users[user.ownerId];

        if (sender) {
          return [...senders, sender];
        }

        return senders;
      }, []);
    }
  )((_state, experiment) => experiment);

const emptySenders: User[] = [];
type GetAllDelegationTeamLeadersForCurrentUser = (state: YammerState) => User[];
export const getAllDelegationTeamLeadersForCurrentUser: GetAllDelegationTeamLeadersForCurrentUser =
  createCachedSelector(getCurrentDelegationTeams, getFulfilledUsers, (onBehalfOfSenders, users) => {
    emptySenders.length = 0;
    if (!onBehalfOfSenders) {
      return emptySenders;
    }

    return Object.values(onBehalfOfSenders).reduce<User[]>((senders, user) => {
      const sender = users[user.ownerId];

      if (sender && user.isOwnerLeader && !user.viewerIsOwner) {
        senders.push(sender);
      }

      return senders;
    }, emptySenders);
  })((_state) => 'cache');

type GetDelegationManagerUsersForCurrentUser = (state: YammerState) => User[];
export const getDelegationManagerUsersForCurrentUser: GetDelegationManagerUsersForCurrentUser = createCachedSelector(
  getCurrentDelegationTeams,
  getCurrentUser,
  getFulfilledUsers,
  (onBehalfOfSenders, currentUser, users) => {
    if (!onBehalfOfSenders) {
      return [currentUser];
    }

    return Object.values(onBehalfOfSenders).reduce((senders, user) => {
      const sender = users[user.ownerId];
      const senderHasPermission = user.viewerCanManageTeam || sender?.id === currentUser.id;

      if (sender && senderHasPermission) {
        return [...senders, sender];
      }

      return senders;
    }, []);
  }
)((_state) => 'cache');

type CanViewerManageTeam = (state: YammerState, leaderId: User['id']) => boolean;
export const canViewerManageTeam: CanViewerManageTeam = (state, leaderId) => {
  const delegationTeams = getCurrentDelegationTeams(state);
  if (!delegationTeams) {
    return false;
  }

  return !!delegationTeams[leaderId]?.viewerCanManageTeam;
};

type CanViewerPostToStoryline = (state: YammerState, leaderId: User['id'] | undefined) => boolean;
export const canViewerPostToStoryline: CanViewerPostToStoryline = (state, leaderId) => {
  if (!leaderId) {
    return false;
  }

  const delegationTeams = getCurrentDelegationTeams(state);
  if (!delegationTeams) {
    return false;
  }

  return !!delegationTeams[leaderId]?.viewerCanPostToStoryline;
};

type HasNetworkWideAudienceForLeader = (state: YammerState, leaderId: User['id'] | undefined) => boolean;
export const hasNetworkWideAudienceForLeader: HasNetworkWideAudienceForLeader = (state, leaderId) => {
  const delegationTeams = getCurrentDelegationTeams(state);
  if (!leaderId || !delegationTeams) {
    return false;
  }

  return !!delegationTeams[leaderId]?.hasNetworkWideAudience;
};

type CanViewerPostStorylineAnnouncementForLeader = (state: YammerState, leaderId: User['id'] | undefined) => boolean;
export const canViewerPostStorylineAnnouncementForLeader: CanViewerPostStorylineAnnouncementForLeader = (
  state,
  leaderId
) => {
  const isOverrideAndDisableStorylineAnnouncements =
    getExperimentTreatment(state, Experiment.OverrideAndDisableStorylineAnnouncements) === 'enabled';

  if (!leaderId || isOverrideAndDisableStorylineAnnouncements) {
    return false;
  }

  const delegationTeams = getCurrentDelegationTeams(state);
  if (!delegationTeams) {
    return false;
  }

  return delegationTeams[leaderId]?.viewerCanPostAnnouncementToStoryline;
};

type CanPostOnBehalfOfUser = (state: YammerState, onBehalfOfUserId: User['id']) => boolean;
export const canPostOnBehalfOfUser: CanPostOnBehalfOfUser = (state, onBehalfOfUserId) => {
  const delegationTeamUsers = getDelegationTeamUsersForCurrentUserStoryline(
    state,
    Experiment.StorylinePostOnBehalfOfFiles
  );

  return !!delegationTeamUsers.find((user) => user.id === onBehalfOfUserId);
};

type CanManageOnBehalfOfUser = (state: YammerState, onBehalfOfUserId: User['id'] | undefined) => boolean;
export const canManageOnBehalfOfUser: CanManageOnBehalfOfUser = (state, onBehalfOfUserId) => {
  if (!onBehalfOfUserId) {
    return false;
  }
  const delegationTeamUsers = getDelegationManagerUsersForCurrentUser(state);

  return !!delegationTeamUsers.find((user) => user.id === onBehalfOfUserId);
};
